import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import BudgetTypeService from '../../services/budgetType.service';
import { useTranslation } from '../../hooks/useTranslation';

const initialFormState = {
  name: '',
  is_active: true
};

const BudgetTypesConfig = () => {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [editingType, setEditingType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadTypes = async () => {
    setLoading(true);
    try {
      const response = await BudgetTypeService.getAll();
      setTypes(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching budget types:', error);
      setSnackbar({ open: true, message: t('budgets.errorLoadingTypes'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name || '',
        is_active: type.is_active ?? true
      });
    } else {
      setEditingType(null);
      setFormData(initialFormState);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormState);
    setEditingType(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setSnackbar({ open: true, message: t('budgets.nameRequired'), severity: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      if (editingType) {
        await BudgetTypeService.update(editingType.id, {
          name: formData.name.trim(),
          is_active: formData.is_active
        });
        setSnackbar({ open: true, message: t('budgets.budgetTypeUpdated'), severity: 'success' });
      } else {
        await BudgetTypeService.create({
          name: formData.name.trim(),
          is_active: formData.is_active
        });
        setSnackbar({ open: true, message: t('budgets.budgetTypeCreated'), severity: 'success' });
      }
      handleCloseDialog();
      loadTypes();
    } catch (error) {
      console.error('Error saving budget type:', error);
      const message = error.response?.data?.message || t('budgets.errorSavingType');
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (type) => {
    if (!window.confirm(t('budgets.setInactiveConfirm', { name: type.name }))) {
      return;
    }

    try {
      await BudgetTypeService.update(type.id, {
        name: type.name,
        is_active: false
      });
      setSnackbar({ open: true, message: t('budgets.budgetTypeInactive'), severity: 'success' });
      loadTypes();
    } catch (error) {
      console.error('Error updating budget type:', error);
      const message = error.response?.data?.message || t('budgets.errorUpdatingType');
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const tableContent = useMemo(() => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      );
    }

    if (!types.length) {
      return (
        <Box py={5} textAlign="center">
          <Typography color="text.secondary">{t('budgets.noActiveTypes')}</Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('common.name')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type.id} hover>
                <TableCell>{type.name}</TableCell>
                <TableCell align="right">
                  <Tooltip title={t('common.edit')}>
                    <IconButton onClick={() => handleOpenDialog(type)} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('budgets.markInactive')}>
                    <IconButton onClick={() => handleDeactivate(type)} size="small" color="error">
                      <ArchiveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [loading, types, t]);

  return (
    <Box p={2}>
      <Card>
        <CardHeader
          title={t('budgets.budgetTypes')}
          subheader={t('budgets.manageTypes')}
          action={
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadTypes}
              >
                {t('budgets.refresh')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                {t('budgets.newType')}
              </Button>
            </Box>
          }
        />
        <CardContent>
          {tableContent}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingType ? t('budgets.editBudgetType') : t('budgets.newBudgetType')}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label={t('common.name')}
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  color="primary"
                />
              }
              label={t('budgets.active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? t('budgets.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BudgetTypesConfig;

